import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useDeletePayslip, usePaysliplist, usePublishPayslips, useSendPayslipEmails } from '../../hooks/usePayslip';
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Collapse,
  Box,
  Grid,
  Container,
  Paper,
  Chip,
  Fade,
  Zoom,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Tooltip,
  TablePagination,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from '@mui/icons-material/Search';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PublishIcon from '@mui/icons-material/Publish';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import { SELECTED_MENU_PROPS } from '../StyledComponents/StyledComponent';
import PayslipPDF from '../pdf/PayslipPdf';
import { useAlert } from '../../context/AlertContext';
import { useDeleteConfirmation } from '../../context/DeleteConfirmationContext';
import { deletPayslip, previewPayslipEmails } from '../../api';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX } from '../common/tableStyles';

// ---- FORTHEYE soft-card tokens (shared with the rest of the portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};

const Paylogs = () => {
  const [searchName, setSearchName] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(''); // '' | PUBLISHED | DRAFT | OUTDATED
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [debouncedSearch, setDebouncedSearch] = useState(searchName);

  // Debounce Search — one box matches employee name OR employee ID (backend
  // ORs the two), so no separate employee-ID field/state is needed here.
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchName);
      setPage(0); // Reset to first page on search
    }, 500);
    return () => clearTimeout(handler);
  }, [searchName]);

  // Reset page when filters change
  React.useEffect(() => {
    setPage(0);
  }, [selectedMonth, selectedYear, selectedStatus]);

  // Arrived from the attendance "LOP" tile → focus this employee's payslips for
  // the given year. The backend payslip search matches employee name, so we seed
  // searchName (not the employee number).
  const location = useLocation();
  React.useEffect(() => {
    const empName = location.state?.employeeName;
    const yr = location.state?.year;
    if (empName) setSearchName(empName);
    if (yr) setSelectedYear(String(yr));
  }, [location.state]);

  const { data: queryData, error, isLoading } = usePaysliplist({
    page: page + 1, // API is 1-indexed
    page_size: rowsPerPage,
    month: selectedMonth,
    year: selectedYear,
    search: debouncedSearch,
    status: selectedStatus,
  });

  const payslipdata = queryData?.results || [];
  const totalCount = queryData?.count || 0;
  // Status breakdown for the current filter, aggregated server-side over ALL
  // matching payslips (not just the visible page).
  const statusCounts = queryData?.status_counts || { PUBLISHED: 0, DRAFT: 0, OUTDATED: 0 };
  // Click a count chip to show only that status; click again to clear.
  const toggleStatusFilter = (s) => setSelectedStatus((prev) => (prev === s ? '' : s));

  const deletepay = useDeletePayslip();
  const { mutate: publishPayslips } = usePublishPayslips();
  const [expandedRow, setExpandedRow] = useState(null);
  const { showWarning, showSuccess, showError, showInfo, hideAlert } = useAlert();
  const { showDeleteConfirmation } = useDeleteConfirmation();
  const [, setIsPublishing] = useState(false);
  const queryClient = useQueryClient();
  const [deletingPeriod, setDeletingPeriod] = useState(null);
  // Single global Set of selected payslip IDs across all periods.
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const togglePeriodAll = (group, allCurrentlySelected) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      group.payslips.forEach((p) => {
        if (allCurrentlySelected) next.delete(p.id); else next.add(p.id);
      });
      return next;
    });
  };

  const selectedInPeriod = (group) =>
    group.payslips.filter((p) => selectedIds.has(p.id));

  // ----- Payslip email (preview-then-send) -----
  const { mutate: sendPayslipEmails, isPending: isSendingEmails } = useSendPayslipEmails();
  const [emailDialog, setEmailDialog] = useState({
    open: false, loading: false, payload: null, subtitle: '',
    recipients: [], total: 0, sendable: 0, payslips: [],
    fromEmail: '',
  });

  // Fetch the recipient preview for a selection and open the review dialog.
  // `payslips` are the full row objects so we can render the actual payslip(s).
  const openEmailPreview = async (payload, subtitle, payslips = []) => {
    setEmailDialog({
      open: true, loading: true, payload, subtitle, recipients: [], total: 0, sendable: 0, payslips,
      fromEmail: '',
    });
    try {
      const data = await previewPayslipEmails(payload);
      setEmailDialog((d) => ({
        ...d, loading: false,
        recipients: data.recipients || [], total: data.total || 0, sendable: data.sendable || 0,
        fromEmail: data.from_email || '',
      }));
    } catch (e) {
      showError(`Could not load preview: ${e?.response?.data?.error || 'Unknown error'}`);
      setEmailDialog((d) => ({ ...d, open: false, loading: false }));
    }
  };

  const closeEmailDialog = () => setEmailDialog((d) => ({ ...d, open: false }));

  // Send after HR has reviewed the recipients in the dialog.
  const confirmSendEmails = () => {
    if (!emailDialog.payload) return;
    sendPayslipEmails(emailDialog.payload, {
      onSuccess: (data) => {
        const parts = [`Emailed ${data.sent_count} payslip(s)`];
        if (data.skipped_count) parts.push(`${data.skipped_count} skipped (no/invalid email)`);
        if (data.failed_count) parts.push(`${data.failed_count} failed`);
        if (data.failed_count) showWarning(parts.join(' · '));
        else showSuccess(parts.join(' · '));
        closeEmailDialog();
      },
      onError: (e) => showError(`Send failed: ${e?.response?.data?.error || 'Unknown error'}`),
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Loading State
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} sx={{ color: ACCENT }} />
        <Typography variant="h6" color="text.secondary">
          Loading payslip data...
        </Typography>
      </Box>
    );
  }

  // Error State
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            ...CARD_SX,
            padding: 4,
            textAlign: 'center',
            maxWidth: '400px',
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Error Loading Data
          </Typography>
          <Typography color="text.secondary">
            {error.message}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // No Client-Side Filtering anymore
  // Group by Period
  const groupedData = payslipdata.reduce((acc, p) => {
    const period = `${p.month} ${p.year}`;
    if (!acc[period]) {
      acc[period] = {
        period: period,
        month: p.month,
        year: p.year,
        payslips: []
      };
    }

    // Backend now returns pre-filtered history (current only), but let's be safe
    const list = Array.isArray(p.deduction_history) ? p.deduction_history : [];
    // The backend already filters this to the relevant one, so we can just take it or the first one.

    acc[period].payslips.push({
      ...p,
      deduction_history: list,
    });
    return acc;
  }, {});

  // Sort groups by year and month (reverse)
  const sortedPeriods = Object.keys(groupedData).sort((a, b) => {
    const [monthA, yearA] = a.split(' ');
    const [monthB, yearB] = b.split(' ');
    if (yearA !== yearB) return yearB - yearA;
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return months.indexOf(monthB) - months.indexOf(monthA);
  });

  const uniqueMonths = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const uniqueYears = [...new Set((payslipdata || []).map(p => String(p.year)))].sort((a, b) => b - a);

  // Publish Handler
  const handlePublish = (payload) => {
    const isBulk = !payload.payslip_id;
    let empNo = '';

    if (!isBulk) {
      const payslip = payslipdata.find(p => p.id === payload.payslip_id);
      empNo = payslip?.employee?.employee_no || payslip?.employee_no || '';
    }

    const confirmMsg = isBulk
      ? `Are you sure you want to PUBLISH all draft payslips for ${payload.month} ${payload.year}?`
      : `Are you sure you want to PUBLISH this individual payslip${empNo ? ` for Employee ${empNo}` : ''}?`;

    if (!window.confirm(confirmMsg)) return;

    const loadingMsg = isBulk
      ? `Publishing all draft payslips for ${payload.month} ${payload.year}...`
      : `Publishing payslip for Employee ${empNo}...`;

    const loadingAlertId = showInfo(loadingMsg, { autoHideDuration: null });

    setIsPublishing(true);
    publishPayslips(payload, {
      onSuccess: (data) => {
        hideAlert(loadingAlertId);
        const successMsg = isBulk
          ? `Successfully published all payslips for ${payload.month} ${payload.year}.`
          : `Successfully published payslip for Employee ${empNo}.`;
        showSuccess(successMsg);
      },
      onError: (error) => {
        hideAlert(loadingAlertId);
        showError(`Failed to publish: ${error?.response?.data?.error || "Unknown error"}`);
      },
      onSettled: () => {
        setIsPublishing(false);
      },
    });
  };

  // Delete Handler
  const handleDelete = (id) => {
    if (!id) {
      showWarning('Invalid ID');
      return;
    }

    const payslip = payslipdata.find(p => p.id === id);
    const empNo = payslip?.employee?.employee_no || payslip?.employee_no || '';

    showDeleteConfirmation(id, () => {
      const loadingAlertId = showInfo(`Deleting payslip ${empNo ? `for Employee ${empNo}` : ''}...`, { autoHideDuration: null });
      deletepay.mutate(id, {
        onSuccess: () => {
          hideAlert(loadingAlertId);
          showSuccess(`Payslip ${empNo ? `for Employee ${empNo}` : ''} deleted successfully.`);
        },
        onError: (error) => {
          hideAlert(loadingAlertId);
          showError(`Failed to delete payslip ${empNo ? `for Employee ${empNo}` : ''}.`);
        }
      });
    });
  };

  // Bulk Delete Handler — deletes the given list of payslip IDs (either the user's
  // selected subset or every payslip in a period). Calls deletPayslip directly
  // (not via the hook) to avoid N success toasts; we batch results into a single alert.
  const handleDeletePeriod = (ids, period) => {
    if (!ids?.length) {
      showWarning('No payslips to delete.');
      return;
    }
    const count = ids.length;

    showDeleteConfirmation(`period:${period}:${count}`, async () => {
      setDeletingPeriod(period);
      const loadingAlertId = showInfo(
        `Deleting ${count} payslip(s) for ${period}...`,
        { autoHideDuration: null }
      );

      try {
        const results = await Promise.allSettled(ids.map((id) => deletPayslip(id)));
        const succeeded = results.filter((r) => r.status === 'fulfilled').length;
        const failed = results.length - succeeded;

        hideAlert(loadingAlertId);

        if (failed === 0) {
          showSuccess(`Deleted ${succeeded} payslip(s) for ${period}.`);
        } else if (succeeded === 0) {
          showError(`Failed to delete payslips for ${period}.`);
        } else {
          showWarning(`Deleted ${succeeded} of ${count} payslips for ${period}. ${failed} failed.`);
        }
      } catch (err) {
        hideAlert(loadingAlertId);
        showError(`Unexpected error deleting payslips for ${period}.`);
      } finally {
        // Clear the attempted IDs from the selection set so the UI resets cleanly.
        setSelectedIds((prev) => {
          const next = new Set(prev);
          ids.forEach((id) => next.delete(id));
          return next;
        });
        queryClient.invalidateQueries(['payslips']);
        setDeletingPeriod(null);
      }
    });
  };

  // Toggle Row Expansion
  const toggleRow = (id) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  // Render Detail Field
  const renderDetailField = (label, value, isHighlight = false, highlightColor = null) => (
    <Grid item xs={6} sm={4}>
      <Box
        sx={{
          padding: 1.5,
          borderRadius: '12px',
          background: isHighlight
            ? highlightColor || `${ACCENT}14`
            : '#f8fafc',
          border: isHighlight
            ? `1.5px solid ${ACCENT}`
            : '1px solid #eef0f4',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: isHighlight ? ACCENT : 'text.secondary',
            fontWeight: isHighlight ? 700 : 400,
            fontSize: { xs: '10px', sm: '11px' },
            display: 'block',
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
        <Typography
          fontWeight={isHighlight ? 700 : 600}
          sx={{
            color: isHighlight ? ACCENT : 'inherit',
            fontSize: { xs: isHighlight ? '15px' : '13px', sm: isHighlight ? '16px' : '14px' },
          }}
        >
          {value}
        </Typography>
      </Box>
    </Grid>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl" disableGutters>
        {/* Header card */}
        <Fade in timeout={800}>
          <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.75,
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  bgcolor: `${ACCENT}14`,
                  color: ACCENT,
                }}
              >
                <ReceiptIcon />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                  Payslip Logs
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  View and manage employee payslips
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Fade>

        {/* Filters Section */}
        <Zoom in timeout={1000}>
          <Paper
            elevation={0}
            sx={{
              ...CARD_SX,
              mb: { xs: 2, md: 3 },
            }}
          >
            <Grid container spacing={2} alignItems="center">
              {/* Search Field */}
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder="Search by employee name or ID..."
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: ACCENT, mr: 1 }} />,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      bgcolor: '#f8fafc',
                      '&:hover fieldset': { borderColor: ACCENT },
                      '&.Mui-focused fieldset': { borderColor: ACCENT },
                    },
                  }}
                />
              </Grid>

              {/* Month Filter */}
              <Grid item xs={6} md={3.5}>
                <FormControl
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      bgcolor: '#f8fafc',
                    },
                  }}
                >
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    label="Month"
                    startAdornment={<FilterListIcon sx={{ color: ACCENT, mr: 1 }} />}
                    MenuProps={SELECTED_MENU_PROPS}
                  >
                    <MenuItem value=""><em>All Months</em></MenuItem>
                    {uniqueMonths.map((m, idx) => (
                      <MenuItem key={idx} value={m}>{m}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Year Filter */}
              <Grid item xs={6} md={3.5}>
                <FormControl
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      bgcolor: '#f8fafc',
                    },
                  }}
                >
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    label="Year"
                    startAdornment={<HistoryIcon sx={{ color: ACCENT, mr: 1 }} />}
                    MenuProps={SELECTED_MENU_PROPS}
                  >
                    <MenuItem value=""><em>All Years</em></MenuItem>
                    {uniqueYears.map((y, idx) => (
                      <MenuItem key={idx} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

            </Grid>

            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`${totalCount} Payslips Found`}
                size="small"
                sx={{
                  bgcolor: '#eef4ff',
                  color: '#2f6bff',
                  fontWeight: 700,
                  border: 'none',
                }}
              />
              <Chip
                label={`Published ${statusCounts.PUBLISHED || 0}`}
                size="small"
                clickable
                onClick={() => toggleStatusFilter('PUBLISHED')}
                sx={{ bgcolor: '#f0fdf4', color: '#16a34a', fontWeight: 700, cursor: 'pointer', border: selectedStatus === 'PUBLISHED' ? '2px solid #16a34a' : '2px solid transparent' }}
              />
              <Chip
                label={`Draft ${statusCounts.DRAFT || 0}`}
                size="small"
                clickable
                onClick={() => toggleStatusFilter('DRAFT')}
                sx={{ bgcolor: '#fffbeb', color: '#f59e0b', fontWeight: 700, cursor: 'pointer', border: selectedStatus === 'DRAFT' ? '2px solid #f59e0b' : '2px solid transparent' }}
              />
              <Chip
                label={`Outdated ${statusCounts.OUTDATED || 0}`}
                size="small"
                clickable
                onClick={() => toggleStatusFilter('OUTDATED')}
                sx={{ bgcolor: '#fef2f2', color: '#ef4444', fontWeight: 700, cursor: 'pointer', border: selectedStatus === 'OUTDATED' ? '2px solid #ef4444' : '2px solid transparent' }}
              />
              {selectedStatus && (
                <Chip
                  label={`Showing: ${selectedStatus} — clear`}
                  size="small"
                  variant="outlined"
                  onDelete={() => setSelectedStatus('')}
                  onClick={() => setSelectedStatus('')}
                  sx={{ fontWeight: 700, cursor: 'pointer', borderColor: '#e5e7eb', color: '#6b7280' }}
                />
              )}
            </Box>
          </Paper>
        </Zoom>

        {/* Centralized Action Bar */}
        {(selectedMonth && selectedYear) && (
          <Fade in timeout={800}>
            <Paper
              elevation={0}
              sx={{
                ...CARD_SX,
                mb: { xs: 2, md: 3 },
                p: { xs: 2, sm: 2.5 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: '13px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <CheckCircleIcon />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#111827' }}>
                    Ready to publish?
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Found {(payslipdata.filter(p => !p.status || p.status === 'DRAFT')).length} draft payslips on this page for {selectedMonth} {selectedYear}
                  </Typography>
                </Box>
              </Box>
              {(payslipdata.filter(p => !p.status || p.status === 'DRAFT')).length > 0 && (
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<PublishIcon />}
                  onClick={() => handlePublish({ month: selectedMonth, year: selectedYear })}
                  sx={{ ...GRADIENT_BTN, px: 3, py: 1.1 }}
                >
                  Publish All Found
                </Button>
              )}
            </Paper>
          </Fade>
        )}

        {/* Grouped Content Section */}
        <Fade in timeout={1200}>
          <Box sx={{ mb: 4 }}>
            {sortedPeriods.map((period) => {
              const group = groupedData[period];
              const draftCount = group.payslips.filter(p => !p.status || p.status === 'DRAFT').length;

              // Adaptive selection state: when nothing in this period is checked,
              // the button deletes the whole period; otherwise it deletes only the picks.
              const periodSelection = selectedInPeriod(group);
              const hasSelection = periodSelection.length > 0;
              const allInPeriodSelected =
                group.payslips.length > 0 &&
                periodSelection.length === group.payslips.length;
              const targetIds = hasSelection
                ? periodSelection.map((p) => p.id)
                : group.payslips.map((p) => p.id);
              const deleteLabel = deletingPeriod === period
                ? 'Deleting...'
                : hasSelection
                  ? `Delete Selected (${periodSelection.length})`
                  : 'Delete';
              const deleteTooltip = hasSelection
                ? `Delete ${periodSelection.length} selected payslip(s)`
                : 'Delete all payslips for this period';

              // Email targets: only PUBLISHED payslips can be mailed. Use the
              // selected published ones if any are checked, else every published
              // slip in the period.
              const publishedInPeriod = group.payslips.filter((p) => p.status === 'PUBLISHED');
              const selectedPublished = periodSelection.filter((p) => p.status === 'PUBLISHED');
              const emailTargets = selectedPublished.length > 0 ? selectedPublished : publishedInPeriod;
              const emailLabel = selectedPublished.length > 0
                ? `Email Selected (${selectedPublished.length})`
                : `Email Published (${publishedInPeriod.length})`;

              return (
                <Accordion
                  key={period}
                  defaultExpanded
                  sx={{
                    mb: 2,
                    borderRadius: '22px !important',
                    overflow: 'hidden',
                    border: '1px solid #eef0f4',
                    boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: ACCENT }} />}
                    sx={{
                      bgcolor: '#fff',
                      color: '#111827',
                      padding: { xs: '12px 16px', sm: '16px 24px' },
                      borderBottom: '1px solid #eef0f4',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2, gap: 1, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <ReceiptIcon />
                        </Box>
                        <Typography variant="h6" fontWeight={800} sx={{ color: '#111827' }}>
                          {period}
                        </Typography>
                        <Chip
                          label={`${group.payslips.length} Slips`}
                          size="small"
                          sx={{ bgcolor: '#eef4ff', color: ACCENT, fontWeight: 700, border: 'none' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {draftCount > 0 && (
                          <Button
                            variant="contained"
                            size="small"
                            disableElevation
                            startIcon={<PublishIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePublish({ month: group.month, year: group.year });
                            }}
                            sx={GRADIENT_BTN}
                          >
                            Publish {draftCount} Drafts
                          </Button>
                        )}
                        <Tooltip title={emailTargets.length ? `Email ${emailTargets.length} published payslip(s)` : 'No published payslips to email'}>
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={emailTargets.length === 0}
                              startIcon={<EmailIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                openEmailPreview(
                                  { payslip_ids: emailTargets.map((p) => p.id) },
                                  `${period} — ${emailTargets.length} payslip(s)`,
                                  emailTargets,
                                );
                              }}
                              sx={{
                                color: ACCENT,
                                borderColor: `${ACCENT}55`,
                                fontWeight: 700,
                                textTransform: 'none',
                                borderRadius: '12px',
                                '&:hover': { borderColor: ACCENT, background: `${ACCENT}0d` },
                                '&.Mui-disabled': { color: ACCENT, borderColor: `${ACCENT}33`, opacity: 0.5 },
                              }}
                            >
                              {emailLabel}
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={deleteTooltip}>
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={deletingPeriod === period}
                              startIcon={
                                deletingPeriod === period
                                  ? <CircularProgress size={16} sx={{ color: '#ef4444' }} />
                                  : <DeleteSweepIcon />
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePeriod(targetIds, period);
                              }}
                              sx={{
                                color: '#ef4444',
                                borderColor: '#ef444455',
                                fontWeight: 700,
                                textTransform: 'none',
                                borderRadius: '12px',
                                '&:hover': { borderColor: '#ef4444', background: '#fef2f2' },
                                '&.Mui-disabled': {
                                  color: '#ef4444',
                                  borderColor: '#ef444433',
                                  opacity: 0.7,
                                },
                              }}
                            >
                              {deleteLabel}
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ padding: 0 }}>
                    <TableContainer sx={{ ...TABLE_PAPER_SX, borderRadius: 0, border: 'none' }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox" sx={HEAD_CELL_SX}>
                              <Checkbox
                                indeterminate={hasSelection && !allInPeriodSelected}
                                checked={allInPeriodSelected}
                                onChange={() => togglePeriodAll(group, allInPeriodSelected)}
                                disabled={deletingPeriod === period}
                                sx={{
                                  color: ACCENT,
                                  '&.Mui-checked': { color: ACCENT },
                                  '&.MuiCheckbox-indeterminate': { color: ACCENT },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={HEAD_CELL_SX}>Employee Name</TableCell>
                            <TableCell align="center" sx={HEAD_CELL_SX}>Net Pay</TableCell>
                            <TableCell align="center" sx={HEAD_CELL_SX}>Status</TableCell>
                            <TableCell align="center" sx={HEAD_CELL_SX}>Details</TableCell>
                            <TableCell align="center" sx={HEAD_CELL_SX}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.payslips.map((payslip) => (
                            <React.Fragment key={payslip.id}>
                              <TableRow
                                sx={{
                                  ...TABLE_ROW_SX,
                                  background: selectedIds.has(payslip.id)
                                    ? `${ACCENT}0d`
                                    : 'transparent',
                                }}
                              >
                                <TableCell padding="checkbox" sx={BODY_CELL_SX}>
                                  <Checkbox
                                    checked={selectedIds.has(payslip.id)}
                                    onChange={() => toggleOne(payslip.id)}
                                    disabled={deletingPeriod === period}
                                    sx={{
                                      color: ACCENT,
                                      '&.Mui-checked': { color: ACCENT },
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={BODY_CELL_SX}>
                                  <Typography fontWeight={600}>{payslip.employee}</Typography>
                                </TableCell>
                                <TableCell align="center" sx={BODY_CELL_SX}>
                                  <Typography fontWeight={700} color={ACCENT}>₹{payslip.net_pay}</Typography>
                                </TableCell>
                                <TableCell align="center" sx={BODY_CELL_SX}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                    <Chip
                                      label={payslip.status || 'DRAFT'}
                                      size="small"
                                      sx={{
                                        fontWeight: 700, fontSize: '11px', border: 'none',
                                        ...(payslip.status === 'PUBLISHED'
                                          ? { bgcolor: '#f0fdf4', color: '#16a34a' }
                                          : payslip.status === 'OUTDATED'
                                            ? { bgcolor: '#fef2f2', color: '#ef4444' }
                                            : { bgcolor: '#fffbeb', color: '#f59e0b' }),
                                      }}
                                    />
                                    {payslip.email_sent_at && (
                                      <Tooltip title={`Emailed on ${payslip.email_sent_at}`}>
                                        <Chip
                                          icon={<EmailIcon sx={{ fontSize: 13 }} />}
                                          label="Emailed"
                                          size="small"
                                          sx={{
                                            fontWeight: 700, fontSize: '10px', height: 20, border: 'none',
                                            bgcolor: '#f0fdf4', color: '#16a34a',
                                            '& .MuiChip-icon': { color: '#16a34a', ml: '4px' },
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="center" sx={BODY_CELL_SX}>
                                  <IconButton
                                    onClick={() => toggleRow(payslip.id)}
                                    size="small"
                                    sx={{ color: ACCENT }}
                                  >
                                    {expandedRow === payslip.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                  </IconButton>
                                </TableCell>
                                <TableCell align="center" sx={BODY_CELL_SX}>
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                    {payslip.status !== 'PUBLISHED' ? (
                                      <Tooltip title="Publish individually">
                                        <IconButton
                                          size="small"
                                          onClick={() => handlePublish({ payslip_id: payslip.id })}
                                          sx={{ color: '#16a34a', bgcolor: '#f0fdf4', '&:hover': { background: '#dcfce7' } }}
                                        >
                                          <PublishIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    ) : (
                                      // PUBLISHED rows: email the payslip to the employee
                                      // (opens a preview of the recipient first).
                                      <Tooltip title="Send payslip to email">
                                        <IconButton
                                          size="small"
                                          onClick={() => openEmailPreview(
                                            { payslip_id: payslip.id },
                                            `${payslip.employee} — ${payslip.month} ${payslip.year}`,
                                            [payslip],
                                          )}
                                          sx={{ color: ACCENT, bgcolor: `${ACCENT}14`, '&:hover': { background: `${ACCENT}22` } }}
                                        >
                                          <EmailIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Delete record">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDelete(payslip.id)}
                                        sx={{ color: '#ef4444', bgcolor: '#fef2f2', '&:hover': { background: '#fee2e2' } }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>

                              {expandedRow === payslip.id && (
                                <TableRow>
                                  <TableCell colSpan={6} sx={{ padding: 0 }}>
                                    <Collapse in={expandedRow === payslip.id}>
                                      <Box
                                        sx={{
                                          margin: { xs: 1, sm: 2 },
                                          padding: { xs: 2, sm: 3 },
                                          background: '#fcfdff',
                                          borderRadius: '16px',
                                          border: '1px solid #eef0f4',
                                        }}
                                      >
                                        <Grid container spacing={2}>
                                          {renderDetailField('Basic', `₹${payslip.basic}`)}
                                          {renderDetailField('HRA', `₹${payslip.hra}`)}
                                          {renderDetailField('Conveyance', `₹${payslip.conveyance}`)}
                                          {renderDetailField('Medical', `₹${payslip.medical}`)}
                                          {renderDetailField('Spl Allowance', `₹${payslip.special_allowance}`)}
                                          {Number(payslip.employer_pf) > 0 && renderDetailField('Employer PF', `₹${payslip.employer_pf}`)}
                                          {Number(payslip.food_allowance) > 0 && renderDetailField('Food Allowance', `₹${payslip.food_allowance}`)}
                                          {payslip.travel_allowance > 0 && renderDetailField('Travel Allowance', `₹${payslip.travel_allowance}`)}
                                          {payslip.recharge > 0 && renderDetailField('Recharge', `₹${payslip.recharge}`)}
                                          {payslip.telephone_allowance > 0 && renderDetailField('Telephone Allowance', `₹${payslip.telephone_allowance}`)}
                                          {payslip.exam_increment > 0 && renderDetailField('Exam Increment', `₹${payslip.exam_increment}`)}
                                          {payslip.bonus > 0 && renderDetailField('Bonus', `₹${payslip.bonus}`)}
                                          {payslip.incentive > 0 && renderDetailField('Incentive', `₹${payslip.incentive}`)}
                                          {payslip.other_earnings > 0 && renderDetailField('Other Earnings', `₹${payslip.other_earnings}`)}
                                          {payslip.lop_deduction > 0 && renderDetailField('Loss of Pay', `₹${payslip.lop_deduction}`, false, 'rgba(217, 83, 79, 0.1)')}
                                          {renderDetailField('Total Earnings', `₹${payslip.total_earnings}`, true, 'rgba(67, 233, 123, 0.1)')}
                                          {renderDetailField('Total Deduction', `₹${payslip.total_deductions}`, true, 'rgba(220, 53, 69, 0.1)')}
                                          {renderDetailField('Net Pay', `₹${payslip.net_pay}`, true)}

                                          <Grid item xs={12}>
                                            <Box sx={{
                                              p: { xs: 1, sm: 3 },
                                              background: '#fff',
                                              borderRadius: '16px',
                                              border: `1px dashed ${ACCENT}`,
                                              display: 'flex',
                                              flexDirection: 'column', 
                                              alignItems: 'center', 
                                              gap: 2,
                                              overflowX: 'auto',
                                              width: '100%',
                                              maxWidth: '100%'
                                            }}>
                                              <PayslipPDF data={payslip} />
                                            </Box>
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            {sortedPeriods.length === 0 && (
              <Paper elevation={0} sx={{ ...CARD_SX, p: 8, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 64, height: 64, borderRadius: '18px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ReceiptIcon sx={{ fontSize: 34 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>No Payslips Found</Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>Try adjusting your filters or generating new payslips from the Payroll Process page.</Typography>
              </Paper>
            )}
          </Box>
        </Fade>

        {/* Pagination Controls */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            ...CARD_SX,
            p: 0,
            mt: 2,
          }}
        />

        {/* ---- Payslip email preview / confirm dialog ---- */}
        <Dialog
          open={emailDialog.open}
          onClose={closeEmailDialog}
          maxWidth="lg"
          fullWidth
          scroll="paper"
          // The global admin header is position:fixed at z-index 1301; MUI's default
          // dialog z-index is 1300, so the header was painting over the title bar.
          // Lift the whole modal (backdrop + paper) above the header. See HRP-FE-19.
          sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
          PaperProps={{
            sx: {
              maxHeight: { xs: '100dvh', sm: 'calc(100vh - 32px)' },
              height: { xs: '100dvh', sm: 'auto' },
              width: { xs: '100%', sm: 'calc(100% - 32px)' },
              m: { xs: 0, sm: 2 },
              borderRadius: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, flexShrink: 0, py: 2, px: { xs: 2, sm: 3 } }}>
            <EmailIcon sx={{ color: ACCENT }} /> Send Payslip Email
          </DialogTitle>
          <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
            {emailDialog.subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {emailDialog.subtitle}
              </Typography>
            )}
            {emailDialog.loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (() => {
              const recips = emailDialog.recipients;
              const missing = recips.filter((r) => r.status === 'missing').length;
              const dup = recips.filter((r) => r.status === 'duplicate').length;
              const severity = emailDialog.sendable === 0 ? 'error' : (missing || dup) ? 'warning' : 'success';
              const isSingle = recips.length === 1;
              const sample = recips.find((r) => r.sendable) || recips[0];
              return (
                <>
                  {/* From / To / Subject — same style as the invitation composer */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>From:</strong> {emailDialog.fromEmail || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>To:</strong>{' '}
                      {isSingle
                        ? (sample?.email || '— none —')
                        : `${emailDialog.sendable} employee mailbox(es) — each receives their own payslip`}
                    </Typography>
                    {sample?.subject && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Subject:</strong> {sample.subject}
                      </Typography>
                    )}
                    {isSingle && sample?.attachment_name && (
                      <Typography variant="body2">
                        <strong>Attachment:</strong> 📎 {sample.attachment_name}
                      </Typography>
                    )}
                  </Box>

                  <Alert severity={severity} icon={severity === 'warning' ? <WarningAmberIcon /> : undefined} sx={{ mb: 2 }}>
                    <strong>{emailDialog.sendable}</strong> of {emailDialog.total} payslip(s) will be emailed.
                    {missing ? ` ${missing} have no valid email and will be skipped.` : ''}
                    {dup ? ` ${dup} share a duplicate address — review carefully, the wrong person may receive a payslip.` : ''}
                  </Alert>

                  {/* The actual email body that will be delivered to the employee */}
                  {sample?.html && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#374151' }}>
                        Email {isSingle ? '' : '(sample — personalised per employee)'}
                      </Typography>
                      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                        <iframe
                          title="payslip-email-preview"
                          srcDoc={sample.html}
                          style={{ width: '100%', height: 'min(360px, 45dvh)', border: 'none' }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Recipient safety table (bulk — single recipient already shown above) */}
                  {!isSingle && (
                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee', borderRadius: 2, maxHeight: 320, mb: 2 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Emp ID</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Net Pay</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {recips.map((r) => {
                            const chip = r.status === 'ok'
                              ? { label: 'Will send', color: '#2e7d32', bg: '#e8f5e9' }
                              : r.status === 'duplicate'
                                ? { label: 'Duplicate', color: '#ed6c02', bg: '#fff4e5' }
                                : { label: 'No email', color: '#d32f2f', bg: '#fdecea' };
                            return (
                              <TableRow key={r.payslip_id}>
                                <TableCell>{r.name}</TableCell>
                                <TableCell>{r.employee_no}</TableCell>
                                <TableCell sx={{ color: r.email ? 'inherit' : '#d32f2f' }}>{r.email || '— none —'}</TableCell>
                                <TableCell>₹{r.net_pay}</TableCell>
                                <TableCell align="center">
                                  <Chip size="small" label={chip.label} sx={{ bgcolor: chip.bg, color: chip.color, fontWeight: 700 }} />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* The attached payslip(s) themselves */}
                  {emailDialog.payslips.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#374151' }}>
                        Attached payslip{isSingle ? '' : 's'}
                      </Typography>
                      {emailDialog.payslips.length === 1 ? (
                        <Box sx={{ maxHeight: '60vh', overflow: 'auto', border: '1px solid #eee', borderRadius: 2, bgcolor: '#fafafa' }}>
                          <PayslipPDF data={emailDialog.payslips[0]} />
                        </Box>
                      ) : (
                        emailDialog.payslips.map((p) => (
                          <Accordion
                            key={p.id}
                            TransitionProps={{ unmountOnExit: true }}
                            sx={{ '&:before': { display: 'none' }, border: '1px solid #eee', borderRadius: '8px !important', mb: 1, overflow: 'hidden' }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography fontWeight={600}>{p.employee} — {p.month} {p.year}</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                              <Box sx={{ maxHeight: '60vh', overflow: 'auto', bgcolor: '#fafafa' }}>
                                <PayslipPDF data={p} />
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        ))
                      )}
                    </Box>
                  )}
                </>
              );
            })()}
          </DialogContent>
          <DialogActions
            sx={{
              p: 2,
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              gap: { xs: 1, sm: 0 },
              '& > :not(style) ~ :not(style)': { ml: { xs: 0, sm: 1 } },
            }}
          >
            <Button onClick={closeEmailDialog} disabled={isSendingEmails} sx={{ textTransform: 'none', width: { xs: '100%', sm: 'auto' }, minHeight: 44 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={isSendingEmails ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendIcon />}
              disabled={emailDialog.loading || isSendingEmails || emailDialog.sendable === 0}
              onClick={confirmSendEmails}
              sx={{ ...GRADIENT_BTN, px: 2, width: { xs: '100%', sm: 'auto' }, minHeight: 44 }}
            >
              {isSendingEmails ? 'Sending…' : `Send to ${emailDialog.sendable} mailbox(es)`}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box >
  );
};

export default Paylogs;
